import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { billing } from '@/api/billing';
import { useOrg } from '@/tenancy/OrgContext';

export const CREDITS_QUERY_KEY = ['billing', 'balance'] as const;

export default function CreditsWidget() {
  const { currentOrgId } = useOrg();

  const { data, isLoading } = useQuery({
    queryKey: [...CREDITS_QUERY_KEY, currentOrgId],
    queryFn: () => billing.balance(currentOrgId!),
    enabled: !!currentOrgId,
    // Mild background refresh so the widget reflects newly minted credits after a purchase
    // or a fresh analysis charge without needing a hard reload.
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  if (!currentOrgId) return null;

  if (isLoading || !data) {
    return (
      <div className="credits-widget" aria-busy="true">
        <span className="credits-icon" aria-hidden>💎</span>
        <span className="muted small">…</span>
      </div>
    );
  }

  const balance = data.balance;
  const tone =
    balance === 0 ? 'credits-widget-zero' : balance < 10 ? 'credits-widget-low' : '';

  if (balance === 0) {
    return (
      <Link to="/billing" className={`credits-widget ${tone}`} title="No credits left">
        <span className="credits-icon" aria-hidden>💎</span>
        <span className="credits-amount">
          <span className="credits-count">0</span>
          <span className="credits-unit"> credits</span>
        </span>
        <span className="credits-cta">Buy credits</span>
      </Link>
    );
  }

  return (
    <Link
      to="/billing"
      className={`credits-widget ${tone}`}
      title={balance < 10 ? 'Low credits — top up soon' : 'Credits available'}
    >
      <span className="credits-icon" aria-hidden>💎</span>
      <span className="credits-amount">
        <span className="credits-count">{balance}</span>
        <span className="credits-unit"> {balance === 1 ? 'credit' : 'credits'}</span>
      </span>
      <span className="credits-cta credits-cta-secondary">Buy more</span>
    </Link>
  );
}
